import prettyBytes from 'pretty-bytes';
import { isArray, isNil, omit, uniq } from 'lodash/fp';
import { Duration } from 'luxon';
import {
    ASSET_NOT_FOUND_BY_SLUG,
    CHAT_GPT_ERROR,
    COMPILATION_FAILED_WITH_DATASET_ERROR,
    COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS,
    ENTITY_NOT_FOUND_OR_NOT_ACCESSIBLE,
    GOOGLE_AUTHENTICATION_FAILED,
    MAX_FILE_SIZE_EXCEEDED,
    MISSING_TABLE,
    SNOWFLAKE_OBJECT_NOT_FOUND_OR_NOT_ACCESSIBLE,
    TIMEOUT,
    UNKNOWN_FUNCTIONS
} from 'app/store/__generated__/errors';
import { ERROR_IN_REFERENCE_TYPE } from 'app/store/__generated__/types';
import { ErrorLevel } from 'app/lib/constants';
import { getErrorLevelFallback } from './errorLevelFallback';
import { CUSTOM_TRANSFORMATION_ERRORS, ERROR_IN_REFERENCE } from './constants';

/**
 * Component independent enrichment of interpolation data for i18next.
 */
const COMMON_ERROR_MAPPINGS = {
    [ASSET_NOT_FOUND_BY_SLUG]: (data) => {
        if (data._type.includes('GetSpace')) {
            return { ...data, context: 'space' };
        }
        if (data._type.includes('GetView')) {
            return { ...data, context: 'view' };
        }
        return data;
    },
    [CHAT_GPT_ERROR]: (data) => {
        if (data.code) {
            return { ...data, context: data.code };
        }
        return data;
    },
    [ENTITY_NOT_FOUND_OR_NOT_ACCESSIBLE]: (data) => {
        if (data._type.includes('GetOrFetchDataSource')) {
            return { ...data, context: 'datasource' };
        }
        return data;
    },
    [GOOGLE_AUTHENTICATION_FAILED]: (data) => {
        if (data.errorCode) {
            return { ...data, context: data.errorCode };
        }
        return data;
    },
    [MISSING_TABLE]: (data) => {
        const { table, qualification } = data;
        const qualifiedTable = qualification
            ? qualification._database + '.' + qualification._schema + '.' + table
            : table;
        return { ...data, qualifiedTable };
    },
    [SNOWFLAKE_OBJECT_NOT_FOUND_OR_NOT_ACCESSIBLE]: (data) => {
        const { table, database, schema } = data;
        const qualifiedTable = `${database}.${schema}.${table}`;
        return { ...data, qualifiedTable };
    },
    [MAX_FILE_SIZE_EXCEEDED]: (data) => {
        const maxFileSizeHumanReadable = prettyBytes(data.maxFileSize);
        return { ...data, maxFileSizeHumanReadable };
    },
    [UNKNOWN_FUNCTIONS]: (data) => ({
        ...data,
        functionNames: uniq(data.functionNames.split(', '))
    }),
    [TIMEOUT]: (data) => {
        const time = Duration.fromISO(data.timeoutAt).toHuman();
        return { ...data, time };
    }
};

/**
 * @param {*} code
 * @param {*} messages
 * @param {ErrorLevel} [errorLevel]
 * @returns an response error dressed in our typicall error structure which is translatable via i18next
 */
export function mapResponseStatus(code, messages, errorLevel) {
    switch (code) {
        case 413:
            return {
                _type: 'errors:PayloadTooLarge',
                level: errorLevel ?? ErrorLevel.ERROR
            };
        case 422:
            return {
                _type: 'errors:UnprocessableEntity',
                message: messages?.[0],
                level: errorLevel ?? ErrorLevel.ERROR,
                detail: messages?.[1]
            };
        case 500:
            return {
                _type: 'errors:ServerError',
                message: messages?.[0],
                level: errorLevel ?? ErrorLevel.ERROR,
                detail: messages?.[1]
            };
        default:
            return {
                _type: 'errors:UnmappedResponseCode',
                code,
                message: messages?.[0],
                level: errorLevel ?? ErrorLevel.ERROR,
                detail: messages?.[1]
            };
    }
}

export const MULTIPLE_ERRORS = 'MultipleErrors';

/**
 * @param {*} failures
 * @returns an response error dressed in our typicall error structure which is translatable via i18next
 */
export function wrapFailures(failures) {
    return {
        _type: MULTIPLE_ERRORS,
        errors: failures
    };
}

/**
 * @param {*} actionPayload
 * @param {ErrorLevel} [errorLevel]
 * @returns returns an typical error structure with `_type` and `level` properties
 */
export function extractError(actionPayload, errorLevel) {
    if (actionPayload.data?._type) {
        // regular v1 error
        return withErrorLevel(actionPayload.data, errorLevel);
    }
    if (actionPayload.failures?.length) {
        // dedicated frontend-api style
        if (actionPayload.failures.length === 1) {
            return withErrorLevel(actionPayload.failures[0], errorLevel);
        }
        return withErrorLevel(wrapFailures(actionPayload.failures), errorLevel);
    }

    // no error payload
    return mapResponseStatus(actionPayload.code, actionPayload.messages);
}

const wrappingErrors = new Set([
    COMPILATION_FAILED_WITH_DATASET_ERROR,
    COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS,
    CUSTOM_TRANSFORMATION_ERRORS,
    MULTIPLE_ERRORS
]);

/**
 * @param {*} error
 * @returns true if the given error is a wrapper around one or multiple concrete errors
 */
function isWrappingOneOrMoreErrors(error) {
    const errorName = extractErrorName(error);
    return wrappingErrors.has(errorName);
}

function withErrorLevelsForNestedErrors(error, errorLevel) {
    const errorName = extractErrorName(error);
    const fallbackErrorLevel = errorLevel ?? getErrorLevelFallback(errorName);
    if (
        errorName === COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS ||
        errorName === CUSTOM_TRANSFORMATION_ERRORS ||
        errorName === MULTIPLE_ERRORS
    ) {
        const errorsWithLevel = error.errors.map((e) => withErrorLevel(e, fallbackErrorLevel));
        return {
            ...error,
            errors: errorsWithLevel,
            level: fallbackErrorLevel
        };
    }
    if (errorName === COMPILATION_FAILED_WITH_DATASET_ERROR) {
        const errorWithLevel = withErrorLevel(error.error, fallbackErrorLevel);
        return { ...error, error: errorWithLevel, level: fallbackErrorLevel };
    }
}

/**
 * Adds the error level and recursively adds error levels to eventually contained errors
 * @param {*} error
 * @param {ErrorLevel} [errorLevel]
 * @returns returns an object with an added `level` property
 */
function withErrorLevel(error, errorLevel) {
    if (isWrappingOneOrMoreErrors(error)) {
        return withErrorLevelsForNestedErrors(error, errorLevel);
    }

    if (errorLevel !== undefined) {
        return { ...error, level: errorLevel };
    }

    const errorName = extractErrorName(error);
    return { ...error, level: getErrorLevelFallback(errorName) };
}

/**
 * @param {*} error
 * @returns the error name without the java namespace
 */
export function extractErrorName(error) {
    if (isArray(error)) {
        return null;
    }

    if (!error._type) {
        console.warn('Error with no _type', error);

        return null;
    }

    return error._type.split('.').pop();
}

/**
 * Translates an JSON error as coming from the backend to a i18next usable structure.
 *
 * @param {*} customMappings
 * @returns
 */
export function errorToI18n(componentPrefix = null, customMappings = null) {
    const mappings = {
        ...COMMON_ERROR_MAPPINGS,
        ...customMappings
    };
    return (payloadData) => {
        if (isNil(payloadData)) {
            return;
        }
        const defaultKey = extractErrorName(payloadData);

        // find the key in the component translations with fallback to the default key
        const i18nKeys = componentPrefix
            ? [`${componentPrefix}.${defaultKey.split(':').pop()}`, defaultKey]
            : defaultKey;

        const mapping = mappings[defaultKey];
        const data = mapping ? mapping(payloadData) : payloadData;
        return {
            key: i18nKeys,
            data: { ...omit(['_type', 'level'], data), ns: ['common', 'errorFallbacks'] }
        };
    };
}

/**
 * Translates an JSON error with no component specific error translations.
 *
 * @param {*} t
 * @param {*} payloadData
 * @returns
 */
export function translateError(t, payloadData) {
    if (!payloadData) {
        return null;
    }
    const i18n = errorToI18n()(payloadData);
    return t(i18n.key, i18n.data);
}

/**
 * Some errors are just container, wrapping one or more concrete errors.
 * This method unwraps them to either a single error or an array of errors.
 *
 * @param {*} error
 * @returns either a single error or an array of multiple errors
 */
export function unwrapErrors(error) {
    if (!isWrappingOneOrMoreErrors(error)) {
        return error;
    }
    const errorName = extractErrorName(error);
    if (
        errorName === COMPILATION_FAILED_WITH_TRANSFORMATION_ERRORS ||
        errorName === CUSTOM_TRANSFORMATION_ERRORS
    ) {
        if (error.errors.length === 1) {
            return error.errors[0];
        }
        return error.errors;
    }
    if (errorName === COMPILATION_FAILED_WITH_DATASET_ERROR) {
        const containedError = error.error;
        if (isWrappingOneOrMoreErrors(containedError)) {
            return unwrapErrors(containedError);
        }
        return containedError;
    }
    if (errorName === MULTIPLE_ERRORS) {
        return error.errors;
    }
}

/**
 * @param {*} error
 * @returns true if the error is a reference error
 */
export function isErrorInReference(error) {
    // For some reason the backend does not return the fully qualified name
    return error?._type === ERROR_IN_REFERENCE || error?._type === ERROR_IN_REFERENCE_TYPE;
}

/**
 * @param {*} error
 * @returns true if the error is a standard error object like produced by extractError()
 */
export function isRegularError(error) {
    return Boolean(error._type);
}

/**
 * @param {string} error
 * @param {object} [payload]
 * @param {ErrorLevel} [level]
 * @returns a new error object with the given type, payload and level
 */
export function createError(type, payload = {}, errorLevel) {
    return withErrorLevel({ _type: type, ...payload }, errorLevel);
}
